import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Phone, AlertTriangle, CheckCircle2, XCircle, Clock, Loader2,
  Building2, Server, Shield, Activity, Send, Bell, Mail,
} from 'lucide-react';
import { requestNotificationPermission, showLocalNotification } from '../notifications';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function MobileSupportHub({ token, properties }) {
  const [selectedProperty, setSelectedProperty] = useState('');
  const [systemStatus, setSystemStatus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [incidentForm, setIncidentForm] = useState({ platform_type_id: '', title: '', severity: 'medium', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!selectedProperty) return;
    setLoading(true);
    axios.get(`${BACKEND}/api/system-status?property_id=${selectedProperty}`, { headers })
      .then(r => setSystemStatus(r.data.statuses || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedProperty, headers]);

  useEffect(() => {
    if (showContacts && selectedProperty) {
      axios.get(`${BACKEND}/api/support/sheet/${selectedProperty}`, { headers })
        .then(r => setContacts((r.data.platforms || []).map(p => ({ platform_name: p.platform_name, vendor_name: p.vendor_name, phone: p.support_phone, email: p.support_email }))))
        .catch(() => setContacts([]));
    }
  }, [showContacts, selectedProperty, headers]);

  const submitIncident = async () => {
    if (!incidentForm.title || !incidentForm.platform_type_id) { toast.error('Fill in platform and title'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/admin/incidents`, {
        property_id: selectedProperty,
        platform_type_id: incidentForm.platform_type_id,
        title: incidentForm.title,
        severity: incidentForm.severity,
        description: incidentForm.description,
      }, { headers });
      toast.success('Incident reported');
      setIncidentOpen(false);
      setIncidentForm({ platform_type_id: '', title: '', severity: 'medium', description: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const STATUS_ICON = {
    operational: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
    degraded: <AlertTriangle className="h-4 w-4 text-amber-500" />,
    down: <XCircle className="h-4 w-4 text-red-500" />,
    maintenance: <Clock className="h-4 w-4 text-blue-500" />,
  };

  return (
    <div className="p-4 pb-20 space-y-4" data-testid="mobile-support-hub">
      {/* Property selector - prominent */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Property</label>
        <Select value={selectedProperty} onValueChange={setSelectedProperty}>
          <SelectTrigger className="h-12 text-base" data-testid="mobile-property-select">
            <SelectValue placeholder="Select a property..." />
          </SelectTrigger>
          <SelectContent>
            {(properties || []).map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!selectedProperty ? (
        <div className="text-center py-12">
          <Building2 className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">Select a property to view system status and support contacts.</p>
        </div>
      ) : loading ? (
        <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : (
        <>
          {/* Quick Actions */}
          <div className="grid grid-cols-3 gap-2">
            <Button variant="outline" className="h-14 flex-col gap-1" onClick={() => setIncidentOpen(!incidentOpen)}>
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <span className="text-xs">Report Issue</span>
            </Button>
            <Button variant="outline" className="h-14 flex-col gap-1" onClick={() => setShowContacts(!showContacts)}>
              <Phone className="h-5 w-5 text-blue-500" />
              <span className="text-xs">Contacts</span>
            </Button>
            <Button variant="outline" className="h-14 flex-col gap-1" onClick={async () => {
              const perm = await requestNotificationPermission();
              if (perm === 'granted') {
                showLocalNotification('Notifications Enabled', 'You\'ll be alerted when systems go down.');
                toast.success('Push notifications enabled');
              } else if (perm === 'denied') {
                toast.error('Notifications blocked — enable in browser settings');
              } else {
                toast.info('Notification permission pending');
              }
            }}>
              <Bell className="h-5 w-5 text-emerald-500" />
              <span className="text-xs">Alerts</span>
            </Button>
          </div>

          {/* Incident form */}
          {incidentOpen && (
            <div className="border rounded-xl p-4 bg-card space-y-3">
              <h3 className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-500" /> Report Incident</h3>
              <Select value={incidentForm.platform_type_id} onValueChange={v => setIncidentForm(p => ({ ...p, platform_type_id: v }))}>
                <SelectTrigger className="h-10"><SelectValue placeholder="Select platform..." /></SelectTrigger>
                <SelectContent>
                  {systemStatus.map(s => <SelectItem key={s.platform_type_id} value={s.platform_type_id}>{s.platform_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input value={incidentForm.title} onChange={e => setIncidentForm(p => ({ ...p, title: e.target.value }))} placeholder="What's happening?" className="h-10" />
              <div className="flex gap-2">
                {['low', 'medium', 'high', 'critical'].map(s => (
                  <button key={s} onClick={() => setIncidentForm(p => ({ ...p, severity: s }))}
                    className={`flex-1 py-2 rounded-lg text-xs font-medium capitalize border ${incidentForm.severity === s ? (s === 'critical' ? 'bg-red-100 border-red-300 text-red-700' : s === 'high' ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-blue-100 border-blue-300 text-blue-700') : 'hover:bg-muted/60'}`}>
                    {s}
                  </button>
                ))}
              </div>
              <Textarea value={incidentForm.description} onChange={e => setIncidentForm(p => ({ ...p, description: e.target.value }))} placeholder="Details..." rows={2} />
              <Button onClick={submitIncident} disabled={submitting} className="w-full gap-1.5">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit
              </Button>
            </div>
          )}

          {/* Contacts inline */}
          {showContacts && (
            <div className="border rounded-xl p-4 bg-card space-y-3">
              <h3 className="text-sm font-semibold flex items-center gap-2"><Phone className="h-4 w-4 text-blue-500" /> Support Contacts</h3>
              {contacts.length === 0 && !loading ? (
                <p className="text-xs text-muted-foreground text-center py-4">No support contacts configured for this property. Add them in Admin &gt; Support Cards.</p>
              ) : contacts.map((c, i) => (
                <div key={i} className="p-3 rounded-lg bg-muted/40 space-y-2">
                  <span className="text-sm font-medium">{c.platform_name || c.vendor_name || 'Support'}</span>
                  <div className="flex gap-2">
                    {c.phone && <a href={`tel:${c.phone}`} className="flex-1 flex items-center justify-center gap-1.5 p-2 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium"><Phone className="h-3.5 w-3.5" /> Call</a>}
                    {c.email && <a href={`mailto:${c.email}`} className="flex-1 flex items-center justify-center gap-1.5 p-2 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium"><Mail className="h-3.5 w-3.5" /> Email</a>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* System Status */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
              <Activity className="h-3.5 w-3.5" /> System Status
            </h3>
            {systemStatus.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No status data. Systems are presumed operational.</p>
            ) : (
              <div className="space-y-1.5">
                {systemStatus.map(s => (
                  <div key={`${s.property_id}-${s.platform_type_id}`} className="flex items-center gap-3 p-3 rounded-xl border bg-card">
                    {STATUS_ICON[s.status] || STATUS_ICON.operational}
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium block">{s.platform_name}</span>
                      {s.notes && <span className="text-xs text-muted-foreground">{s.notes}</span>}
                    </div>
                    <Badge variant="outline" className={`text-[9px] capitalize ${s.status === 'down' ? 'text-red-600 border-red-200' : s.status === 'degraded' ? 'text-amber-600 border-amber-200' : 'text-emerald-600 border-emerald-200'}`}>
                      {s.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
